import React, { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import Papa from "papaparse";
import { jsPDF } from "jspdf";
import { UploadCloud, FileText, ArrowLeft } from "lucide-react";
import { format, parse, isWeekend, addDays } from "date-fns";
import { ptBR } from "date-fns/locale";

import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/utils";

export default function Extrato() {
  const [csvData, setCsvData] = useState([]);
  const [fileName, setFileName] = useState("");
  const [agency, setAgency] = useState("");
  const [account, setAccount] = useState("");
  const [holder, setHolder] = useState("");
  const [paginaFinal, setPaginaFinal] = useState("9"); // Estado para o número da página final
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setFileName(file.name);

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        // Normaliza as colunas, removendo possíveis espaços
        const rows = results.data.map((row) => {
          // Verifica as colunas do novo formato (Crédito e Débito ajustado)
          const creditoValor = parseValor(row["Crédito (R$)"] || "0");
          // O débito já pode vir com sinal negativo no novo formato
          const debitoValor = parseValor(row["Débito ajustado (R$)"] || row["Débito (R$)"] || "0");
          
          // Processa a data para mover lançamentos de final de semana para o próximo dia útil
          const dataOriginal = row["Data"];
          const dataAjustada = ajustarParaDiaUtil(dataOriginal);
          
          return {
            Data: dataAjustada,
            DataOriginal: dataOriginal, // Manter a data original para referência se necessário
            Descricao: row["Descrição"],
            Credito: creditoValor,
            Debito: debitoValor, // Já deve estar negativo no novo formato
            Saldo: 0, // A coluna de saldo não é mais usada
          };
        });
        
        // Filtrar linhas onde tanto o crédito quanto o débito são zeros
        // Considerando que um valor pode ser -0.0, 0, 0.0, ou muito próximo a zero por erro de arredondamento
        const rowsFiltrados = rows.filter(row => {
          // Verificar se o valor absoluto do crédito é maior que um valor mínimo
          const temCredito = Math.abs(row.Credito) > 0.001;
          // Verificar se o valor absoluto do débito é maior que um valor mínimo
          const temDebito = Math.abs(row.Debito) > 0.001;
          
          // Manter a linha se pelo menos um dos valores não for zero
          return temCredito || temDebito;
        });
        
        // Agrupar lançamentos pela mesma data ajustada
        const mapaLancamentos = {};
        
        rowsFiltrados.forEach(row => {
          if (!mapaLancamentos[row.Data]) {
            mapaLancamentos[row.Data] = [];
          }
          mapaLancamentos[row.Data].push(row);
        });
        
        // Manter a ordem original do arquivo (descrecente), mas com linhas de valor zero filtradas
        setCsvData(rowsFiltrados);
      },
    });
  };

  const parseValor = (str) => {
    // Processa números que já vêm em formato float com ponto decimal (ex: "3720.00")
    if (!str) return 0;
    // Remove possíveis símbolos de R$ e espaços, mantém o ponto decimal como está
    return parseFloat(
      str
        .replace(/R\$/g, "")
        .trim()
    );
  };
  
  // Função para ajustar datas de final de semana para o próximo dia útil (segunda-feira)
  const ajustarParaDiaUtil = (dataStr) => {
    // Formato esperado: dd/MM/yyyy
    try {
      if (!dataStr || dataStr.trim() === '') return dataStr;
      
      // Converter a string de data para um objeto Date usando date-fns
      const dataObj = parse(dataStr, 'dd/MM/yyyy', new Date());
      
      if (isNaN(dataObj.getTime())) {
        console.error("Data inválida:", dataStr);
        return dataStr;
      }
      
      // Verificar se é final de semana
      if (isWeekend(dataObj)) {
        console.log(`Data ${dataStr} é final de semana, ajustando para próximo dia útil`);
        
        // Se for sábado (6), adiciona 2 dias para segunda
        // Se for domingo (0), adiciona 1 dia para segunda
        const diaDaSemana = dataObj.getDay();
        const diasParaAdicionar = diaDaSemana === 6 ? 2 : 1;
        const novaData = addDays(dataObj, diasParaAdicionar);
        
        // Verificar se após o ajuste ainda é final de semana (caso especial de feriados)
        const dataAjustada = format(novaData, 'dd/MM/yyyy');
        
        // Garantir que a data ajustada não é final de semana novamente (recursivamente)
        return ajustarParaDiaUtil(dataAjustada);
      }
      
      // Se não for final de semana, retorna a data original
      return dataStr;
    } catch (error) {
      console.error("Erro ao processar data:", error, "para a data:", dataStr);
      return dataStr; // Em caso de erro, mantém a data original
    }
  };

    // Estados para assets Base64
  const [logoB64, setLogoB64] = useState(""); // Estado para carregar fontes em base64
  const [ubuntuB64, setUbuntuB64] = useState("");
  const [robotoRegB64, setRobotoRegB64] = useState("");
  const [robotoSemiLightB64, setRobotoSemiLightB64] = useState("");
  const [robotoBoldB64, setRobotoBoldB64] = useState("");
  const [interB64, setInterB64] = useState("");
  const [manropeB64, setManropeB64] = useState("");
  const [manropeLightB64, setManropeLightB64] = useState("");
  const [openSansRegB64, setOpenSansRegB64] = useState(""); // Nova fonte para o símbolo R$
  const [calendarIconB64, setCalendarIconB64] = useState("");

  // Logo em Base64 será carregado em tempo de execução
  useEffect(() => {
    const logoUrl = "/logo.png";

    fetch(logoUrl)
      .then((res) => {
        if (!res.ok) throw new Error(`Logo not found: ${res.status}`);
        return res.blob();
      })
      .then(
        (blob) =>
          new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(blob);
          })
      )
      .then((dataUrl) => {
        const payload = dataUrl.replace(/^data:image\/png;base64,/, "");
        setLogoB64(payload);
      })
      .catch(console.error);
  }, []);

  // Carrega fonte Ubuntu para uso no PDF
  useEffect(() => {
    fetch("/fonts/Ubuntu-Regular.ttf")
      .then((res) => {
        if (!res.ok) throw new Error(`Font not found: ${res.status}`);
        return res.blob();
      })
      .then(
        (blob) =>
          new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(blob);
          })
      )
      .then((dataUrl) => {
        const payload = dataUrl.replace(/^data:.*;base64,/, "");
        setUbuntuB64(payload);
      })
      .catch(console.error);
  }, []);

  // Carrega fonte Roboto Regular para uso no PDF
  useEffect(() => {
    fetch("/fonts/Roboto-Regular.ttf")
      .then((res) => {
        if (!res.ok) throw new Error(`Font not found: ${res.status}`);
        return res.blob();
      })
      .then((blob) =>
        new Promise((resolve) => {
          const fr = new FileReader();
          fr.onloadend = () => resolve(fr.result);
          fr.readAsDataURL(blob);
        })
      )
      .then((dataUrl) =>
        setRobotoRegB64(dataUrl.replace(/^data:.*;base64,/, ""))
      )
      .catch(console.error);
  }, []);

  // Carrega fonte Roboto SemiCondensed Light para uso no PDF
  useEffect(() => {
    fetch("/fonts/Roboto_SemiCondensed-Light.ttf")
      .then((res) => {
        if (!res.ok) throw new Error(`Font not found: ${res.status}`);
        return res.blob();
      })
      .then((blob) =>
        new Promise((resolve) => {
          const fr = new FileReader();
          fr.onloadend = () => resolve(fr.result);
          fr.readAsDataURL(blob);
        })
      )
      .then((dataUrl) =>
        setRobotoSemiLightB64(dataUrl.replace(/^data:.*;base64,/, ""))
      )
      .catch(console.error);
  }, []);

  // Carrega fonte Roboto Bold para uso no PDF
  useEffect(() => {
    fetch("/fonts/Roboto-Bold.ttf")
      .then((res) => {
        if (!res.ok) throw new Error(`Font not found: ${res.status}`);
        return res.blob();
      })
      .then((blob) =>
        new Promise((resolve) => {
          const fr = new FileReader();
          fr.onloadend = () => resolve(fr.result);
          fr.readAsDataURL(blob);
        })
      )
      .then((dataUrl) => setRobotoBoldB64(dataUrl.replace(/^data:.*;base64,/, "")))
      .catch(console.error);
  }, []);

  // Carrega fonte Inter Regular para uso no PDF
  useEffect(() => {
    fetch("/fonts/Inter_18pt-Regular.ttf")
      .then((res) => {
        if (!res.ok) throw new Error(`Font not found: ${res.status}`);
        return res.blob();
      })
      .then((blob) =>
        new Promise((resolve) => {
          const fr = new FileReader();
          fr.onloadend = () => resolve(fr.result);
          fr.readAsDataURL(blob);
        })
      )
      .then((dataUrl) => setInterB64(dataUrl.replace(/^data:.*;base64,/, "")))
      .catch(console.error);
  }, []);

  // Carrega fonte Manrope-Regular para uso nos símbolos de navegação
  useEffect(() => {
    fetch("/fonts/Manrope-Regular.ttf")
      .then((res) => {
        if (!res.ok) throw new Error(`Font not found: ${res.status}`);
        return res.blob();
      })
      .then((blob) =>
        new Promise((resolve) => {
          const fr = new FileReader();
          fr.onloadend = () => resolve(fr.result);
          fr.readAsDataURL(blob);
        })
      )
      .then((dataUrl) => setManropeB64(dataUrl.replace(/^data:.*;base64,/, "")))
      .catch(console.error);
  }, []);
  
  // Carrega fonte Manrope-Light para uso nas setas de navegação
  useEffect(() => {
    fetch("/fonts/Manrope-Light.ttf")
      .then((res) => {
        if (!res.ok) throw new Error(`Manrope-Light.ttf not found: ${res.status}`);
        return res.blob();
      })
      .then((blob) =>
        new Promise((resolve) => {
          const fr = new FileReader();
          fr.onloadend = () => resolve(fr.result);
          fr.readAsDataURL(blob);
        })
      )
      .then((dataUrl) => setManropeLightB64(dataUrl.replace(/^data:.*;base64,/, "")))
      .catch(console.error);
  }, []);
  
  // Carrega OpenSans Regular (para o símbolo R$)
  useEffect(() => {
    fetch("/fonts/OpenSans-Regular.ttf")
      .then((res) => {
        if (!res.ok) throw new Error(`OpenSans-Regular.ttf not found: ${res.status}`);
        return res.blob();
      })
      .then((blob) =>
        new Promise((resolve) => {
          const fr = new FileReader();
          fr.onloadend = () => resolve(fr.result);
          fr.readAsDataURL(blob);
        })
      )
      .then((dataUrl) => setOpenSansRegB64(dataUrl.replace(/^data:.*;base64,/, "")))
      .catch(console.error);
  }, []);

  // Carrega ícone de calendário
  useEffect(() => {
    fetch("/calendar.png")
      .then((res) => {
        if (!res.ok) throw new Error(`Calendar icon not found: ${res.status}`);
        return res.blob();
      })
      .then((blob) =>
        new Promise((resolve) => {
          const fr = new FileReader();
          fr.onloadend = () => resolve(fr.result);
          fr.readAsDataURL(blob);
        })
      )
      .then((dataUrl) => setCalendarIconB64(dataUrl.replace(/^data:.*;base64,/, "")))
      .catch(console.error);
  }, []);

  const gerarPDF = () => {
    if (csvData.length === 0) return;

    const doc = new jsPDF({ unit: "pt", format: "a4" });
    // Registra a fonte Ubuntu dentro do PDF, se carregada
    if (ubuntuB64.length > 100) {
      try {
        doc.addFileToVFS("Ubuntu-Regular.ttf", ubuntuB64);
        doc.addFont("Ubuntu-Regular.ttf", "UbuntuRegular", "normal");
      } catch (e) {
        console.error("Erro ao registrar fonte Ubuntu: ", e);
      }
    }
    // Registra Roboto Regular
    if (robotoRegB64.length > 100) {
      try {
        doc.addFileToVFS("Roboto-Regular.ttf", robotoRegB64);
        doc.addFont("Roboto-Regular.ttf", "RobotoRegular", "normal");
      } catch (e) {
        console.error("Erro ao registrar fonte Ubuntu: ", e);
      }
    }
    // Registra Roboto SemiCondensed Light
    if (robotoSemiLightB64.length > 100) {
      try {
        doc.addFileToVFS("Roboto_SemiCondensed-Light.ttf", robotoSemiLightB64);
        doc.addFont("Roboto_SemiCondensed-Light.ttf", "RobotoSemiLight", "normal");
      } catch (e) {
        console.error("Erro ao registrar fonte Roboto SemiCondensed: ", e);
      }
    }
    // Registra Roboto Bold
    if (robotoBoldB64.length > 100) {
      try {
        doc.addFileToVFS("Roboto-Bold.ttf", robotoBoldB64);
        doc.addFont("Roboto-Bold.ttf", "RobotoBold", "normal");
      } catch (e) {
        console.error("Erro ao registrar fonte Roboto Bold: ", e);
      }
    }
    // Registra Inter Regular
    if (interB64.length > 100) {
      try {
        doc.addFileToVFS("Inter_18pt-Regular.ttf", interB64);
        doc.addFont("Inter_18pt-Regular.ttf", "InterRegular", "normal");
      } catch (e) {
        console.error("Erro ao registrar fonte Inter: ", e);
      }
    }
    // Registra Manrope Regular para símbolos de navegação
    if (manropeB64.length > 100) {
      try {
        doc.addFileToVFS("Manrope-Regular.ttf", manropeB64);
        doc.addFont("Manrope-Regular.ttf", "ManropeRegular", "normal");
      } catch (e) {
        console.error("Erro ao registrar fonte Manrope Regular: ", e);
      }
    }
    
    // Registra Manrope Light para as setas de navegação
    if (manropeLightB64.length > 100) {
      try {
        doc.addFileToVFS("Manrope-Light.ttf", manropeLightB64);
        doc.addFont("Manrope-Light.ttf", "ManropeLight", "normal");
      } catch (e) {
        console.error("Erro ao registrar fonte Manrope Light: ", e);
      }
    }
    // Registra OpenSans Regular para o símbolo R$
    if (openSansRegB64.length > 100) {
      try {
        doc.addFileToVFS("OpenSans-Regular.ttf", openSansRegB64);
        doc.addFont("OpenSans-Regular.ttf", "OpenSansRegular", "normal");
      } catch (e) {
        console.error("Erro ao registrar fonte OpenSans Regular: ", e);
      }
    }
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 30; // margem lateral equivalente a ~50px
    const topMargin = 30; // margem superior da página
    // Nota: valores de altura das linhas são definidos diretamente nos cálculos

    let y = topMargin;

    const drawHeader = (showLogo = false) => {
      if (showLogo && logoB64.length > 100) { // tamanho mínimo para evitar PNG inválido
        const logoHeight = 25;
        const logoWidth = 80; // ajustado ao zoom 100%
        try {
          doc.addImage(logoB64, "PNG", margin, y, logoWidth, logoHeight);
        } catch (err) {
          console.error("Falha ao adicionar logo ao PDF:", err);
        }
      }

      // Título
      doc.setFont("UbuntuRegular", "normal");
      doc.setFontSize(15.70);
      doc.setTextColor("#000000");
      doc.text("Internet Banking Empresarial", pageWidth - margin, y + 22, { align: "right", charSpace: -0.5 }); // charSpace negativo aproxima letras
      y += 50; // espaçamento extra entre logo e primeira linha

      // Linha cinza
      doc.setDrawColor("#d9d9d9");
      doc.line(margin, y, pageWidth - margin, y);
      y += 16; // reduzido para aproximar mais o nome da linha superior

      // Nome empresa + Agência/Conta
      doc.setFontSize(8.3);
      doc.setTextColor("#000000");
      doc.setFont("RobotoRegular", "normal");
      doc.text(holder || "NOME DO TITULAR", margin, y, { charSpace: -0.1 });
      const rightX = pageWidth - margin;
      // Conta (label bold, value normal)
      doc.setFont("RobotoBold", "normal");
      doc.text("Conta:", rightX - doc.getTextWidth(account || "________") - 24, y);
      doc.setFont("RobotoRegular", "normal");
      doc.text(account || "________", rightX, y, { align: "right" });
      // Agência (label bold, value normal)
      const agenciaX = rightX - 130;
      doc.setFont("RobotoBold", "normal");
      doc.text("Agência:", agenciaX, y);
      doc.setFont("RobotoRegular", "normal");
      doc.text(agency || "____", agenciaX + doc.getTextWidth("Agência:" ) + 2, y);
      
      y += 11;

      // Linha cinza
      doc.line(margin, y, pageWidth - margin, y);
      y += 10;
    };

    const newPage = (fromDateHeader = false) => {
      // Antes de mudar de página, se estamos mudando devido a um cabeçalho de data
      // adicionamos um retângulo cinza fino no final da página atual
      if (fromDateHeader) {
        // Calcular posição para o retângulo cinza fino no final da página atual
        const pageHeight = doc.internal.pageSize.getHeight();
        const bottomMargin = 30;
        const retHeight = 2; // altura do retângulo fino reduzida para 2 pontos
        
        // Adicionar o retângulo cinza fino no final da página atual
        doc.setFillColor("#eeeeee");
        doc.rect(margin, pageHeight - bottomMargin - retHeight, pageWidth - margin * 2, retHeight, "F");
      }
      
      doc.addPage();
      y = 35; // Espaçamento maior do topo quando vier de um cabeçalho de data
    };

    drawHeader(true);

    let currentDate = "";

    // Tratamento especial para o primeiro item
    let isFirstItem = true;
    
    csvData.forEach((row, idx) => {
      const pageHeight = doc.internal.pageSize.getHeight();
      const bottomMargin = 5;
      const needsHeader = row.Data !== currentDate;
      
      // CRUCIAL: Para o primeiro item, sempre começar na primeira página
      if (isFirstItem) {
        isFirstItem = false;
      } 
      // Para outros itens, verificar se cabe na página atual
      else {
        // IMPORTANTE: O código a seguir é a chave para resolver o problema!
        // PERMITIR que o cabeçalho de data fique sozinho no final de uma página
        if (needsHeader) {
          // Se o cabeçalho não cabe, nova página
          if (y + 22 > pageHeight - bottomMargin) { // só o retangulo cinza
            // Passar true para indicar que estamos mudando de página devido a um cabeçalho de data
            newPage(true);
          }
          // Se o cabeçalho cabe, não importa se o item seguinte vai caber ou não
          // Isso permitirá que o cabeçalho ocupe o espaço em branco no final da página
        }
        // Se não é um novo cabeçalho, verificar se cabe na página
        else if (y + 30 > pageHeight - bottomMargin) {
          newPage(false); // Indicamos que não é devido a um cabeçalho de data
        }
      }

      if (row.Data !== currentDate) {
        // Cabeçalho de data com barra cinza-claro
        if (currentDate !== "") {
          y -= 20; // reduzido o espaço a remover entre blocos de data
        }
        currentDate = row.Data;
        doc.setFillColor("#eeeeee"); // Tonalidade de cinza mais clara, conforme a primeira imagem
        doc.rect(margin, y, pageWidth - margin * 2, 22, "F"); // altura reduzida
        doc.setTextColor("#000000");
        doc.setFont("InterRegular", "normal");
        doc.setFontSize(8);
        // Formatar a data usando date-fns - já garantimos que não é final de semana
        // Usamos parse para garantir que a data é interpretada corretamente
        let dateStr = format(parse(currentDate, 'dd/MM/yyyy', new Date()), "EEEE, dd 'de' MMMM 'de' yyyy", { locale: ptBR });
        
        // Remover o sufixo '-feira' dos dias da semana
        dateStr = dateStr
          .replace("segunda-feira", "Segunda")
          .replace("terça-feira", "Terça")
          .replace("quarta-feira", "Quarta")
          .replace("quinta-feira", "Quinta")
          .replace("sexta-feira", "Sexta")
          .replace("sábado", "Sábado")
          .replace("domingo", "Domingo");
          
        // Primeira letra maiúscula (já está garantido no replace acima, mas mantido para seguraça)
        const dateTxt = dateStr.charAt(0).toUpperCase() + dateStr.slice(1);
        doc.text(dateTxt, margin + 50, y + 14); // Espaçamento aumentado para 60 (era 46)
        // ícone calendário (imagem PNG)
        if (calendarIconB64) {
          const iconWidth = 14;
          const iconHeight = 14;
          doc.addImage(calendarIconB64, 'PNG', margin + 15, y + 4, iconWidth, iconHeight); // posição do ícone mantida
        }
        y += 46; // consistente com lineHeight para uniformidade
      }

      // Descrição
      doc.setFont("RobotoRegular", "normal");
      doc.setFontSize(8.44);
      doc.setTextColor("#000000");
      doc.text(row.Descricao, margin + 10, y);

      // Tipo + Valor
      const isCredito = row.Credito > 0;
      const label = isCredito ? "CREDITO" : "DEBITO";
      // label em preto
      doc.setTextColor("#000000");
      doc.text(label, pageWidth - margin - 165, y);
      // valor colorido
      doc.setTextColor(isCredito ? "#007a33" : "#FF0000"); // Vermelho mais intenso para valores negativos
      
      // Nova formatação para valores negativos com sinal apenas antes do R$
      // Precisamos separar o símbolo R$ (em OpenSans) do valor numérico (na fonte atual)
      const valorNumerico = isCredito 
        ? row.Credito.toLocaleString("pt-BR", { minimumFractionDigits: 2 })
        : Math.abs(row.Debito).toLocaleString("pt-BR", { minimumFractionDigits: 2 });
      
      // Posição para alinhamento à direita
      const posX = pageWidth - margin - 20;
      
      // Calcular largura do valor numérico para posicionar corretamente
      const larguraValor = doc.getStringUnitWidth(valorNumerico) * doc.internal.getFontSize() / doc.internal.scaleFactor;
      
      // Abordagem garantindo alinhamento vertical do símbolo R$
      // 1. Definir a cor apropriada para todos os elementos
      const corValor = isCredito ? "#007a33" : "#FF0000";
      doc.setTextColor(corValor);
      
      // 2. Posições fixas para cada elemento - deslocados mais para a direita
      const posicaoSinal = pageWidth - margin - 75;   // Posição para o sinal negativo (muito próximo ao R$)
      const posicaoSimbolo = pageWidth - margin - 73;   // Posição para o símbolo R$ (SEMPRE MESMA POSIÇÃO)
      
      // 3. Renderizar o sinal negativo antes do R$ (sem deslocar o R$)
      if (!isCredito) {
        doc.setFont("OpenSansRegular", "normal");
        doc.text("-", posicaoSinal, y);
      }
      
      // 4. Renderizar o símbolo R$ na mesma posição vertical sempre
      doc.setFont("OpenSansRegular", "normal");
      doc.text("R$", posicaoSimbolo, y);
      
      // 3. Renderizar o valor sem espaços extras (praticamente colado ao símbolo)
      // Redução drástica do espaçamento entre o símbolo e o valor
      const posicaoValor = pageWidth - margin - 61; // Espaçamento mínimo
      doc.setFont("RobotoRegular", "normal");
      doc.text(valorNumerico, posicaoValor, y);

      // linha separadora
      if (idx < csvData.length - 1 && row.Data === csvData[idx + 1].Data) {
        doc.setDrawColor("#d9d9d9");
        doc.setLineWidth(0.8); // aumentar mais a espessura da linha
        doc.line(margin, y + 8, pageWidth - margin, y + 8); // reduzido o espaço
        doc.setLineWidth(0.1); // resetar para o padrão
      }
      y += 34; // espaçamento fixo para manter uniformidade (lineHeight - 2)
    });

    // Linha separadora fina após o último lançamento
    doc.setDrawColor("#d9d9d9");
    doc.setLineWidth(0.8); // aumentar mais a espessura da linha
    doc.line(margin, y - 24, pageWidth - margin, y - 24);
    doc.setLineWidth(0.1); // resetar para o padrão

    // Adicionar bloco de paginação - retangulo cinza com setas e número da página
    y -= 23; // valor negativo maior para aproximar o bloco da linha separadora

    // Função para calcular o espaço necessário para o rodapé
    const calcularEspacoRodape = () => {
      // Altura total necessária para o bloco de paginação + rodapé completo
      return 40 + 15 + 7*4 + 15; // Bloco paginação + espaçamento + linhas do rodapé + margem
    };
    
    // Verificar se há espaço suficiente para o rodapé após a paginação
    const pageHeight = doc.internal.pageSize.getHeight();
    const espacoRestante = pageHeight - y;
    const espacoNecessario = calcularEspacoRodape();
    
    // Se não houver espaço suficiente para o rodapé, iniciar nova página
    if (espacoRestante < espacoNecessario) {
      doc.addPage();
      y = 20; // Recomeça no topo da nova página
    }
    
    // Bloco cinza de paginação
    const paginationBlockHeight = 40;
    doc.setFillColor("#eeeeee"); // Tonalidade de cinza mais clara, igual ao cabeçalho
    doc.rect(margin, y, pageWidth - margin * 2, paginationBlockHeight, "F");
    
    // Abordagem com ajuste fino da centralização visual do número entre as setas
    // Definir posições horizontais para as setas - deslocadas mais à direita
    const leftArrowX = pageWidth - margin - 60; // posição da seta esquerda (mais à direita)
    const rightArrowX = pageWidth - margin - 20; // posição da seta direita (mais à direita)
    const baseY = y + 25; // posição vertical comum para todos elementos
    
    // 1. Desenhar seta esquerda com Manrope Light
    doc.setFont("ManropeLight", "normal");
    doc.setFontSize(13);
    doc.setTextColor("#000000");
    doc.text("<", leftArrowX, baseY);
    
    // 2. Desenhar seta direita com Manrope Light
    doc.text(">", rightArrowX, baseY);
    
    // 3. Número com ajuste para a direita para corresponder ao visual de referência
    // Usando o valor personalizado definido pelo usuário
    doc.setFont("ManropeRegular", "normal");
    doc.setFontSize(9);
    
    // Calcular o centro matemático com deslocamento fino
    const centerX = (leftArrowX + rightArrowX) / 2 + 4.5; // +3 desloca um pouco mais para a direita
    doc.text(paginaFinal.toString(), centerX, baseY, { align: "center" });
    doc.setFont("InterRegular", "normal"); // restaurar fonte original
    
    // Espaçamento e linha separadora final
    y += paginationBlockHeight + 10; // espaçamento depois do bloco de paginação
    doc.setDrawColor("#d9d9d9");
    doc.line(margin, y, pageWidth - margin, y); // linha com espessura normal
    
    // Espaçamento mínimo antes das informações de contato
    y += 15; // Reduzido ao mínimo para aproximar ainda mais o conteúdo do rodapé da linha
    
    // Informações das centrais de atendimento em duas colunas
    doc.setFontSize(5.5); // Diminuindo o tamanho da fonte do rodapé
    doc.setTextColor("#000000");
    
    // Coluna esquerda
    const leftCol = margin;
    
    // Título em negrito
    doc.setFont("RobotoBold", "normal");
    doc.text("Central de Atendimento Santander Empresarial", leftCol, y);
    
    // Volta para fonte regular para os demais textos
    doc.setFont("RobotoRegular", "normal");
    y += 7; // Espaçamento vertical reduzido
    doc.text("4004-2125 (Regiões Metropolitanas)", leftCol, y);
    y += 7; // Espaçamento vertical reduzido
    doc.text("0800 726 2125 (Demais localidades)", leftCol, y);
    y += 7; // Espaçamento vertical reduzido
    doc.text("0800 723 5007 (Pessoas com deficiência auditiva ou de fala)", leftCol, y);
    
    // Coluna direita
    const rightCol = pageWidth / 2;
    const rightY = y - 24; // ajuste do alinhamento considerando o novo espaçamento reduzido
    
    // SAC em negrito
    doc.setFont("RobotoBold", "normal");
    doc.text("SAC", rightCol, rightY);
    doc.setFont("RobotoRegular", "normal");
    doc.text("- Atendimento 24h por dia, todos os dias.", rightCol + 15, rightY);
    
    doc.text("0800 762 7777", rightCol, rightY + 7); // Espaçamento vertical consistente de 7
    doc.text("0800 771 0401 (Pessoas com deficiência auditiva ou de fala)", rightCol, rightY + 14); // 7+7=14
    
    // Ouvidoria em negrito
    doc.setFont("RobotoBold", "normal");
    doc.text("Ouvidoria", rightCol, rightY + 21);
    doc.setFont("RobotoRegular", "normal");
    doc.text("- Das 9h às 18h, de segunda a sexta-feira, exceto feriado.", rightCol + 29, rightY + 21); // 14+7=21
    doc.text("0800 726 0322", rightCol, rightY + 28); // 21+7=28
    doc.text("0800 771 0301 (Pessoas com deficiência auditiva ou de fala)", rightCol, rightY + 35); // 28+7=35

    doc.save(`extrato_${Date.now()}.pdf`);
  };

  const handleSelectFile = () => {
    if (fileInputRef.current) {
      // Limpa o valor do input para permitir selecionar o mesmo arquivo novamente
      fileInputRef.current.value = "";
      fileInputRef.current.click();
    }
  };

  return (
    <div className="container mx-auto">
      {/* Header */}
      <header className="mb-8">
        <div className="flex items-center gap-2 mb-4">
          <Link to={createPageUrl("Operations")}> 
            <Button variant="outline" size="icon">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div className="flex items-center gap-3">
            <FileText className="h-8 w-8 text-lime-600" />
            <h1 className="text-3xl font-bold text-gray-900">Extrato Bancário</h1>
          </div>
        </div>
        <p className="text-gray-500">Carregue um CSV de extrato bancário para gerar um PDF no formato Santander.</p>
      </header>

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Converter Card */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle>Gerador de PDF</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Upload Area */}
              <div>
                <Label htmlFor="file-upload" className="block mb-2">Arquivo CSV do Extrato</Label>
                <div className="flex items-center gap-3">
                  <div
                    className={`flex-1 border rounded-lg p-4 text-center cursor-pointer hover:bg-gray-50 transition-colors ${fileName ? 'border-green-200 bg-green-50' : 'border-gray-200'}`}
                    onClick={handleSelectFile}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      id="file-upload"
                      className="hidden"
                      accept=".csv"
                      onChange={handleFileChange}
                    />
                    {fileName ? (
                      <div className="flex items-center justify-center gap-2 text-green-600">
                        <FileText className="h-5 w-5" />
                        <span className="font-medium truncate max-w-xs">{fileName}</span>
                      </div>
                    ) : (
                      <div className="text-gray-500">
                        <UploadCloud className="h-5 w-5 mx-auto mb-2" />
                        <span>Clique para selecionar um arquivo CSV</span>
                      </div>
                    )}
                  </div>
                  <Button onClick={handleSelectFile}>Selecionar</Button>
                </div>
              </div>

              {/* Titular, Agência & Conta Inputs */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="holder-input" className="block mb-2">Titular</Label>
                  <Input
                    id="holder-input"
                    placeholder="Nome do Titular"
                    value={holder}
                    onChange={(e) => setHolder(e.target.value)}
                    className="max-w-xs"
                  />
                </div>
                <div>
                  <Label htmlFor="agency-input" className="block mb-2">Agência</Label>
                  <Input
                    id="agency-input"
                    placeholder="0000"
                    value={agency}
                    onChange={(e) => setAgency(e.target.value)}
                    className="max-w-xs"
                  />
                </div>
                <div>
                  <Label htmlFor="account-input" className="block mb-2">Conta</Label>
                  <Input
                    id="account-input"
                    placeholder="00000000"
                    value={account}
                    onChange={(e) => setAccount(e.target.value)}
                    className="max-w-xs"
                  />
                </div>
              </div>
              
              {/* Número da Página Final */}
              <div className="mt-4">
                <Label htmlFor="pagina-final-input" className="block mb-2">Número da Página Final</Label>
                <Input
                  id="pagina-final-input"
                  placeholder="9"
                  value={paginaFinal}
                  onChange={(e) => setPaginaFinal(e.target.value)}
                  className="max-w-[80px]"
                  type="number"
                  min="1"
                />
                <p className="text-xs text-gray-500 mt-1">Número que aparecerá na paginação do rodapé</p>
              </div>

              {/* Preview Table */}
              {csvData.length > 0 && (
                <div>
                  <h2 className="text-xl font-semibold mb-2">Pré-visualização ({csvData.length} lançamentos)</h2>
                  <div className="overflow-auto border rounded-md h-60">
                    <table className="min-w-full text-sm">
                      <thead className="bg-gray-100 sticky top-0">
                        <tr>
                          <th className="px-3 py-2 text-left">Data</th>
                          <th className="px-3 py-2 text-left">Descrição</th>
                          <th className="px-3 py-2 text-right">Crédito</th>
                          <th className="px-3 py-2 text-right">Débito</th>
                        </tr>
                      </thead>
                      <tbody>
                        {csvData.map((row, idx) => (
                          <tr key={idx} className="border-b last:border-0">
                            <td className="px-3 py-1 whitespace-nowrap">{row.Data}</td>
                            <td className="px-3 py-1">{row.Descricao}</td>
                            <td className="px-3 py-1 text-right text-green-700">{row.Credito > 0 ? row.Credito.toLocaleString('pt-BR', { minimumFractionDigits: 2 }) : ''}</td>
                            <td className="px-3 py-1 text-right text-red-700">{row.Debito < 0 ? Math.abs(row.Debito).toLocaleString('pt-BR', { minimumFractionDigits: 2 }) : ''}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  {/* Totalizadores */}
                  <div className="mt-4 border rounded-md p-3 bg-gray-50">
                    <div className="grid grid-cols-3 gap-4">
                      {/* Total de Créditos */}
                      <div>
                        <h3 className="text-sm font-medium text-gray-500">Total de Créditos</h3>
                        <p className="text-lg font-semibold text-green-700">
                          {csvData
                            .reduce((sum, row) => sum + (row.Credito || 0), 0)
                            .toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                        </p>
                      </div>
                      
                      {/* Total de Débitos */}
                      <div>
                        <h3 className="text-sm font-medium text-gray-500">Total de Débitos</h3>
                        <p className="text-lg font-semibold text-red-700">
                          {csvData
                            // Filter para usar apenas valores negativos e abs para mostrar positivo
                            .filter(row => row.Debito < 0)
                            .reduce((sum, row) => sum + Math.abs(row.Debito), 0)
                            .toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                        </p>
                      </div>
                      
                      {/* Saldo Final */}
                      <div>
                        <h3 className="text-sm font-medium text-gray-500">Saldo Final</h3>
                        {(() => {
                          const totalCreditos = csvData.reduce((sum, row) => sum + (row.Credito || 0), 0);
                          const totalDebitos = csvData
                            .filter(row => row.Debito < 0)
                            .reduce((sum, row) => sum + Math.abs(row.Debito), 0);
                          // Agora somamos os créditos com os débitos (que já são negativos no CSV)
                          const saldoFinal = totalCreditos - totalDebitos;
                          const textColor = saldoFinal >= 0 ? 'text-green-700' : 'text-red-700';
                          return (
                            <p className={`text-lg font-semibold ${textColor}`}>
                              {saldoFinal.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                            </p>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                  <Button
                      onClick={gerarPDF}
                      className="mt-4 flex items-center gap-2 bg-lime-600 hover:bg-lime-700 text-white"
                    >
                      <FileText className="h-4 w-4" />
                      Gerar PDF
                    </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Info Card */}
        <div>
          <Card>
            <CardHeader>
              <CardTitle>Informações</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <h3 className="font-medium mb-1">O que é isso?</h3>
                  <p className="text-sm text-gray-600">Esta ferramenta converte extratos bancários em PDF no layout oficial do Santander.</p>
                </div>
                <div>
                  <h3 className="font-medium mb-1">Como usar</h3>
                  <ol className="text-sm text-gray-600 space-y-2 list-decimal pl-4">
                    <li>Exporte ou baixe o extrato bancário em formato CSV</li>
                    <li>Faça upload do arquivo CSV no campo ao lado</li>
                    <li>Revise a pré-visualização dos lançamentos</li>
                    <li>Clique em "Gerar PDF" para baixar o documento</li>
                  </ol>
                </div>
                <div className="bg-lime-50 border border-lime-200 rounded-lg p-3 text-lime-800 text-sm">
                  <p><strong>Dica:</strong> Certifique-se de que o CSV contenha as colunas: Data, Descrição, Crédito (R$), Débito (R$) e Saldo (R$).</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
